import { useMemo, useState } from 'react';

import { useAccountProvisioning } from '@tankef/core-react';

import type { Announcement } from '@/domains/experience/dashboard/ui';

import { ANNOUNCEMENT_PRIORITY } from '@/domains/wallet/wallet/model/onboardingValidation';

/**
 * Anuncios del cintillo, derivados del estado que manda el backend.
 *
 * Antes esto reconstruía el estado a mano desde `party.kycLevel` y
 * `account.clabe`, con `hasSignature` y `pagacelAssigned` fijos en `true` (dos
 * alertas que por tanto nunca se pintaban), y con acciones `mailto:` a soporte.
 * El resultado era un cintillo que mentía: a una empresa cuyo representante
 * legal ni había empezado su validación le anunciaba "la activación de tu
 * cuenta CLABE tarda entre 10 min y hasta 1 hora", cuando no había ninguna
 * solicitud en curso ni la habría en horas.
 *
 * `/api/v1/me` ya emitía la respuesta correcta en `account_provisioning`, con
 * un `next_action` que trae etiqueta y ruta. Aquí sólo se transporta:
 *
 *   ready === true            → no hay nada pendiente, cero alertas
 *   next_action presente      → UNA alerta con su label; si trae href, botón
 *                               que lleva al flujo; si no, informativa
 *
 * Una sola alerta y no una lista: el backend ya decidió cuál es el siguiente
 * paso. Apilar cuatro barras obligaba al usuario a adivinar cuál atender.
 *
 * Backoffice: `TODO` sigue vigente — mezclar `GET /v1/announcements` cuando
 * exista, ordenando por `priority` como antes.
 */
export function useOnboardingAnnouncements() {
  const { provisioning, isLoading } = useAccountProvisioning();
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const announcements: Announcement[] = useMemo(() => {
    // Sin dato resuelto no se afirma nada. Emitir una alerta mientras la query
    // vuela era el origen del parpadeo del banner de CLABE en cada refresh.
    if (isLoading || !provisioning) return [];
    if (provisioning.ready) return [];

    const next = provisioning.next_action;
    if (!next) return [];

    const alert: Announcement = {
      id: `provisioning-${next.code}`,
      variant: 'warning',
      message: next.label,
      // El backend manda un único siguiente paso; la banda de onboarding basta
      // para dejarlo por debajo de un incidente crítico y por encima de un
      // aviso promocional.
      priority: ANNOUNCEMENT_PRIORITY.ONBOARDING_KYC,
      source: 'onboarding',
      // No se puede cerrar: es una compuerta, no un aviso.
      dismissible: false,
      ...(next.href
        ? { action: { type: 'navigate' as const, href: next.href, label: next.label } }
        : {}),
    };

    return [alert].filter(a => !dismissed.has(a.id));
  }, [isLoading, provisioning, dismissed]);

  const dismiss = (id: string) => {
    setDismissed(prev => new Set([...prev, id]));
  };

  return { announcements, dismiss };
}
